<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import lumeratecConstants from 'dashboard/constants/globals';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  customAttributes: {
    type: Object,
    default: () => ({}),
  },
  teamIds: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = lumeratecConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });
const agentCpf = ref(props.customAttributes?.cpf || '');
const agentDataNascimento = ref(props.customAttributes?.data_nascimento || '');
const agentSetor = ref(props.customAttributes?.setor || '');
const agentRamal = ref(props.customAttributes?.ramal || '');
const agentTelefone = ref(props.customAttributes?.telefone || '');
const agentAccessibilityProfile = ref(
  props.customAttributes?.accessibility_profile || 'default'
);
const selectedTeamIds = ref(props.teamIds || []);
const nameInput = ref(null);

const isValidCpf = value => {
  const cpf = String(value || '').replace(/\D/g, '');
  if (cpf.length !== 11 || /^(\d)\1{10}$/.test(cpf)) return false;
  const digit = length => {
    const sum = cpf
      .slice(0, length)
      .split('')
      .reduce(
        (total, number, index) => total + Number(number) * (length + 1 - index),
        0
      );
    const remainder = (sum * 10) % 11;
    return remainder === 10 ? 0 : remainder;
  };
  return digit(9) === Number(cpf[9]) && digit(10) === Number(cpf[10]);
};

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
  agentCpf: {
    required,
    validCpf: helpers.withMessage('CPF inválido', isValidCpf),
  },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
  agentCpf,
  agentDataNascimento,
  agentSetor,
  agentRamal,
  agentTelefone,
  selectedTeamIds,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const teamsList = computed(() => store.getters['teams/getTeams'] || []);
const today = new Date().toISOString().slice(0, 10);

onMounted(async () => {
  store.dispatch('teams/get');
  await nextTick();
  nameInput.value?.focus();
});
const getCustomRoles = useMapGetter('customRole/getCustomRoles');
const currentAccount = computed(() => store.getters.getCurrentAccount || {});
const isAdministrator = computed(
  () => currentAccount.value.role === 'administrator'
);

const roles = computed(() => {
  const defaultRoles = isAdministrator.value
    ? [
        {
          id: 'administrator',
          name: 'administrator',
          label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
        },
      ]
    : [];

  if (props.type === 'agent' && !props.customRoleId) {
    defaultRoles.push({
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    });
  }

  const managerPermissions = currentAccount.value.permissions || [];
  const customRoles = getCustomRoles.value
    .filter(
      role =>
        isAdministrator.value ||
        role.permissions.every(permission =>
          managerPermissions.includes(permission)
        )
    )
    .map(role => ({
      id: role.id,
      name: `custom_${role.id}`,
      label: role.name,
    }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
      custom_attributes: {
        cpf: agentCpf.value,
        data_nascimento: agentDataNascimento.value,
        setor: agentSetor.value,
        ramal: agentRamal.value,
        telefone: agentTelefone.value,
        accessibility_profile: agentAccessibilityProfile.value,
      },
      team_ids: selectedTeamIds.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};

const applyCpfMask = value => {
  return value
    .replace(/\D/g, '') // Remove tudo que não é dígito
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})/, '$1-$2')
    .replace(/(-\d{2})\d+?$/, '$1'); // Limita a 11 dígitos
};

const handleCpfInput = e => {
  agentCpf.value = applyCpfMask(e.target.value);
  v$.value.agentCpf.$touch();
};

const applyPhoneMask = value => {
  let r = value.replace(/\D/g, '');
  if (r.length > 11) r = r.slice(0, 11);

  if (r.length === 0) return '';
  if (r.length <= 2) return `(${r}`;
  if (r.length <= 6) return `(${r.slice(0, 2)}) ${r.slice(2)}`;
  if (r.length <= 10)
    return `(${r.slice(0, 2)}) ${r.slice(2, 6)}-${r.slice(6)}`;
  return `(${r.slice(0, 2)}) ${r.slice(2, 7)}-${r.slice(7)}`;
};

const handlePhoneInput = e => {
  agentTelefone.value = applyPhoneMask(e.target.value);
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentCpf.$error }">
          CPF *
          <input
            v-model="agentCpf"
            type="text"
            placeholder="Ex: 000.111.222-33"
            inputmode="numeric"
            autocomplete="off"
            maxlength="14"
            @input="handleCpfInput"
          />
          <span v-if="v$.agentCpf.$error" class="message">
            {{ v$.agentCpf.$errors[0]?.$message }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label>
          Data de Nascimento
          <input v-model="agentDataNascimento" type="date" :max="today" />
        </label>
      </div>

      <div class="w-full">
        <label>
          Setor
          <input v-model="agentSetor" type="text" placeholder="Ex: Vendas" />
        </label>
      </div>

      <fieldset class="p-4 mt-2 w-full rounded-lg border border-n-weak">
        <legend class="px-2 text-sm font-semibold text-n-slate-12">
          Equipes (Times)
        </legend>
        <p v-if="!teamsList.length" class="mb-0 text-sm text-n-slate-11">
          Nenhuma equipe cadastrada.
        </p>
        <label
          v-for="team in teamsList"
          :key="team.id"
          class="flex items-center gap-2 py-1 mb-0"
        >
          <input v-model="selectedTeamIds" type="checkbox" :value="team.id" />
          <span>{{ team.name }}</span>
        </label>
      </fieldset>

      <div class="w-full flex gap-4">
        <label class="w-1/2">
          Ramal
          <input v-model="agentRamal" type="text" placeholder="Ex: 1234" />
        </label>
        <label class="w-1/2">
          Telefone
          <input
            v-model="agentTelefone"
            type="text"
            placeholder="Ex: (11) 99999-9999"
            @input="handlePhoneInput"
          />
        </label>
      </div>

      <fieldset class="p-4 mt-2 w-full rounded-lg border border-n-weak">
        <legend class="px-2 text-sm font-semibold text-n-slate-12">
          Recursos de acessibilidade
        </legend>
        <label for="edit-agent-accessibility-profile">
          Perfil inicial de navegação
          <select
            id="edit-agent-accessibility-profile"
            v-model="agentAccessibilityProfile"
          >
            <option value="default">Padrão</option>
            <option value="lowVision">Baixa visão</option>
            <option value="nonVisual">Navegação não visual</option>
            <option value="keyboard">Operação prioritária por teclado</option>
          </select>
        </label>
      </fieldset>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
